import React from "react";
import clsx from "clsx";
import { buildingSettingsStore } from "features/Building/BuildingSettings/BuildingSettingsStore";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import { skeleton } from "utils";
import { ROUTES_ENUM } from "routes/consts";
import { useLocation, useNavigate } from "react-router";
import { buildingStore } from "store/Building/BuildingStore";
import { operationalEmissionsStore } from "./OperationalEmissionsStore";
import CertificationRow from "./features/CertificationRow";
import EmptyStateOperationalEnergy from "./components/EmptyStateOperationalEnergy";
import useCertificationsList from "features/Building/BuildingSettings/useCertificationsList";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";

const OperationalEmissions = () => {
  const { t } = useTranslation();
  const { hasFeature } = useFeatureAccess();
  const canUseB6Calculator = hasFeature(LicenseFeatureEnum.B6Calculator);
  const activeCertifications = buildingSettingsStore.getActiveCertifications();
  const { certifications: certificationsUIList } = useCertificationsList();
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  async function loadData() {
    if (loading) return;
    setLoading(true);
    operationalEmissionsStore.reset();
    await operationalEmissionsStore.fetchAllCertificationsData(
      buildingStore.buildingID
    );
    setLoading(false);
  }

  React.useEffect(() => {
    (async () => await loadData())();

    return () => {
      operationalEmissionsStore.reset();
    };
  }, [buildingStore.currentBuilding]);

  function handleOpenBuildingSettings() {
    buildingSettingsStore.setOpenCertificationsTab(true);
    buildingSettingsStore.setOpenEditBuilding(true);
  }

  const openB6Dashboard = () => {
    navigate(
      `/${ROUTES_ENUM.BUILDINGS}/${buildingStore.currentBuilding?.id}/${ROUTES_ENUM.EMISSIONS}`,
      { state: location.pathname }
    );
  };

  const getCertificationIndex = (certID: string) => {
    return activeCertifications.findIndex((i) => i === certID);
  };

  function createCertificationRows() {
    return certificationsUIList.map((rec, index) => {
      const currentIndex = getCertificationIndex(rec.id);
      return (
        <CertificationRow
          key={index}
          certId={rec.id}
          index={index}
          certInfo={rec}
          className={clsx({
            hidden: currentIndex === -1,
            "border-t border-gray-300": currentIndex !== 0,
          })}
        />
      );
    });
  }

  if (loading) return <div className="p-6">{skeleton("h-80")}</div>;

  if (!activeCertifications.length)
    return (
      <EmptyStateOperationalEnergy openSettings={handleOpenBuildingSettings} />
    );
  return (
    <div className="p-6">
      <div className="justify-between pb-6 items-center">
        <div className="flex justify-between w-full mb-2">
          <div className="text-xl font-semibold">
            {t("operationalEnergy.title")}
          </div>
          <Button
            width="fit-content"
            onClick={openB6Dashboard}
            disable={!canUseB6Calculator}
            tooltip={
              !canUseB6Calculator
                ? t("operationalEnergy.calculatorNoLicenseTooltip")
                : ""
            }
            className="!h-[35px]"
          >
            {t("operationalEnergy.openCalculator")}
          </Button>
        </div>
        <div className="flex w-full justify-between">
          <div className="text-sm font-normal text-gray-700">
            {t("operationalEnergy.subtitle")}
          </div>
          <div
            className={clsx(
              "pt-1 pl-8 text-nowrap text-indigo-700 font-medium text-base",
              "hover:text-indigo-500 cursor-pointer"
            )}
            onClick={handleOpenBuildingSettings}
          >
            {t("operationalEnergy.activateOtherCertificates")}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-md border border-gray-300 overflow-hidden">
        {createCertificationRows()}
      </div>
    </div>
  );
};

export default observer(OperationalEmissions);
